import {
  START_POST_FORM_ROUTINE,
  CLOSE_POST_FORM,
  UPDATE_POST_FORM_FIELD,
  SAVE_POST_FORM_ROUTINE,
  SELECT_POST_FORM_PLATFORM,
  TOGGLE_POST_FORM_PLATFORM_LINK,
  UPDATE_POST_FORM_VALIDATION,
  CHECK_INSTAGRAM_POST_OVERLAPPING,
  CLOSE_ALL_POST_FORMS,
} from "constants/ActionTypes";
import { createReducer } from "@reduxjs/toolkit";
import { extractCorePost, getPostState, detectPostType } from "utils/posts";
import { getPlatforms } from "utils/accounts";
import { FIELDS, PostFormFieldName } from "constants/PostFormFields";
import { POST_TYPES, PostStatesType } from "constants/PostsView";
import { postPlatformsConfig } from "components/Posts/PostForm/postPlatforms.config";
import { validate } from "components/Posts/PostForm/postFormValidator";
import _ from "lodash";
import {
  FormPlatformType,
  FormValidationErrors,
  FormValidationWarnings,
  LoadedDraft,
  PlatformFields,
  Post,
  PostFormState,
  PostMediaFile,
} from "../../shared/types/post";
import { AccountType } from "../../shared/types/accounts";
import { PlatformSpec } from "../../constants/Platforms";
import { IOverlapSchedule } from "../../components/Posts/PostForm/types";
import { OrderedRecord } from "../../types/redux-store";
import {
  setCoverPreviewUrl,
  setIsFormDirty,
  setSaveData,
} from "../../features/post/bulk-create/bulk-create-post.actions";
import { FormSaveData } from "../../features/post/bulk-create/bulk-create-post.types";
import { StartPostFormSuccessPayload } from "../types";

function getInitalView() {
  return {
    selectedPlatform: "LINKED" as FormPlatformType,
    visibleFields: [],
  };
}

const initialFormState = {
  overlapSchedule: {
    accountName: null,
    overlappingPostId: null,
    nextRunAt: null,
  } as IOverlapSchedule,
  originalPost: {} as Post,
  originalPostState: "",
  postState: "",
  post: {},
  platformFields: {} as PlatformFields,
  accounts: [] as AccountType[],
  platforms: [] as PlatformSpec[],
  view: getInitalView(),
  validation: {
    errors: {},
    warnings: {},
  },
  results: null,
} as const;

const initialState = {
  byId: {},
  allIds: [],
} as OrderedRecord<PostFormState>;

type PostFormsState = typeof initialState;
let formRequestedCount = 1;
const postFormsReducer = createReducer(initialState, {
  [START_POST_FORM_ROUTINE.SUCCESS]: (
    state,
    action: {
      payload: StartPostFormSuccessPayload;
    }
  ) => {
    const {
      post,
      accounts,
      requestType,
      accountSelectionDisabled,
      form,
      requestFormTitle,
    } = action.payload;
    const denormalizedPost = extractCorePost(post);
    const platforms = getPlatforms(accounts);
    const originalPostState = getPostState(post);
    const visibleFields = getVisibleFields("LINKED", platforms);

    // if (originalPostState === "ARCHIVED") {
    //   // this avoids a 'date in the past' error when you are
    //   // "posting again" from archives page
    //   post.when = null;
    // }
    const newForm: PostFormState = {
      ...initialFormState,
      originalPost: denormalizedPost,
      originalPostState: originalPostState,
      post: buildInitialPost(post, accounts),
      postState: originalPostState,
      accounts: accounts,
      platforms: platforms,
      view: {
        ...initialFormState.view,
        visibleFields: visibleFields,
      },
      requestType,
      requestedPost: extractCorePost(post),
      requestedId: formRequestedCount++,
      isDirty: requestType === "create",
      accountSelectionDisabled,
      requestFormTitle: requestFormTitle,
      ...form,
    };

    // Validate form after update
    const [errors, warnings] = validate(
      newForm.postState,
      newForm.post,
      newForm.platformFields,
      newForm.platforms,
      newForm.overlapSchedule
    );

    newForm.validation = {
      errors,
      warnings,
    };

    state.byId[post._id] = newForm;
    if (!state.allIds.includes(post._id)) {
      state.allIds.push(post._id);
    }
    console.log("call START_POST_FORM_ROUTINE.SUCCESS");
    return state;
  },
  [CLOSE_POST_FORM]: (state, action: { payload: { postId: string } }) => {
    const { postId } = action.payload;
    if (postId && state.allIds.includes(postId)) {
      _.remove(state.allIds, (selectedId) => selectedId === postId);
      delete state.byId[postId];
    }
    return state;
  },
  [CLOSE_ALL_POST_FORMS]: (state) => {
    return {
      ...state,
      allIds: [],
      byId: {},
    };
  },
  [UPDATE_POST_FORM_VALIDATION]: (
    state,
    action: {
      payload: {
        postId: string;
        errors: FormValidationErrors;
        warnings: FormValidationWarnings;
      };
    }
  ) => {
    const { postId, errors, warnings } = action.payload;
    state.byId[postId].validation = { errors, warnings };
    return state;
  },
  [SAVE_POST_FORM_ROUTINE.SUCCESS]: (
    state,
    action: {
      payload: {
        postId: string;
        originalPost: Post;
        newPosts: Post[];
      };
    }
  ) => {
    const { postId, originalPost, newPosts } = action.payload;
    const post = state.byId[postId];
    if (post) {
      post.results = { originalPost, newPosts };
    }
    return state;
  },
  [SAVE_POST_FORM_ROUTINE.FAILURE]: (
    state,
    action: {
      payload: string | { message: string; postId?: string };
    }
  ) => {
    type WrapMessage = { message: string; postId?: string };
    const { payload } = action;
    const form = getByIdOrFirst(state, (payload as WrapMessage).postId);
    const id = form.post._id;
    const errorMessage = (payload as WrapMessage).message
      ? (payload as WrapMessage).message
      : payload;
    if (errorMessage) {
      form.validation.errors["SAVE_ERROR"] = {
        errors: [errorMessage],
      };
      state.byId[id] = form;
    }
    return state;
  },
  [SELECT_POST_FORM_PLATFORM]: (
    state,
    action: {
      payload: {
        platformType: FormPlatformType | null;
        postId: string;
      };
    }
  ) => {
    const { platformType, postId } = action.payload;
    const form = getByIdOrFirst(state, postId);
    const id = form.post._id;

    const platforms = form.platforms;
    if (platformType !== null) {
      let fields: string[] = [];
      if (platformType === "LINKED") {
        platforms.map((p) => {
          // eslint-disable-next-line unicorn/prefer-spread
          fields = fields.concat(postPlatformsConfig[p.TYPE].fields);
        });
      } else if (postPlatformsConfig[platformType]) {
        fields = [...postPlatformsConfig[platformType].fields];
      }
      fields = _.uniq(fields);
      form.view.visibleFields = fields;
      form.view.selectedPlatform = platformType;
    }
    state.byId[id] = form;
    return state;
  },
  [TOGGLE_POST_FORM_PLATFORM_LINK]: (
    state,
    action: { payload: { postId: string } | string } | undefined
  ) => {
    const { payload } = action || {};
    const form = getByIdOrFirst(
      state,
      typeof payload === "object" ? payload.postId : undefined
    );
    const id = form.post._id;

    const currentFields = form.platformFields[form.view.selectedPlatform];
    const newPlatformFields = { ...form.platformFields };
    if (currentFields && currentFields.unlinked) {
      delete newPlatformFields[form.view.selectedPlatform];
    } else {
      const unlinkedPlatformDefaultValue = form.view.visibleFields.reduce(
        (acum, field) => {
          _.set(acum, field, _.get(form.post, field));

          return acum;
        },
        { unlinked: true }
      );

      newPlatformFields[form.view.selectedPlatform] =
        unlinkedPlatformDefaultValue;
    }
    form.platformFields = newPlatformFields;
    state.byId[id] = form;
    return state;
  },
  [UPDATE_POST_FORM_FIELD]: (
    state,
    action: {
      payload: {
        postId: string;
        values: Record<PostFormFieldName, unknown>;
      };
    }
  ) => {
    const { postId, values } = action.payload;
    const fields = Object.keys(values) as PostFormFieldName[];

    if (!fields || fields.length === 0) {
      console.log("Invalid call to updateField");
      return state;
    }

    const form = state.byId[postId];

    if (!form) return state;
    const formPost = { ...form.post };
    const newPlatformFields = { ...form.platformFields };

    fields.map((fieldString) => {
      const fieldParts = fieldString.split(".");
      const field = fieldParts.at(-1) as string;
      const value = values[fieldString];

      switch (field) {
        case FIELDS.POST_STATE: {
          form.postState = value as PostStatesType;
          return;
        }
        case FIELDS.ACCOUNTS: {
          form.platforms = getPlatforms(value as AccountType[]);
          form.accounts = value as AccountType[];
          formPost.accounts = value as AccountType[];
          formPost.accountIds = _.map(value as AccountType[], "_id");
          if (formPost.accounts.length === 0) {
            // if there are not selected account reset value to initial
            form.view = getInitalView();
          }
          return;
        }
        case FIELDS.MEDIA: {
          const mediaFiles = value as PostMediaFile[];
          formPost.mediaFiles = mediaFiles;
          formPost.media = null; // deprecated, use mediaFiles instead

          // Remove media file fields so that preview knows it's a text post;
          if (mediaFiles.length === 0) {
            formPost.fileName = null;
            formPost.isVideo = false;
            formPost.origUrl = null;
            formPost.thumbnailUrl = null;
            formPost.url = null;
            formPost.videoPostType = null;
          }

          if (mediaFiles.length > 0) {
            // Need to set the top level of the post since it's used all over
            formPost["altText"] = mediaFiles[0].altText;
            formPost["url"] = mediaFiles[0].url;
            formPost["meta"] = mediaFiles[0].meta;
            formPost["fileName"] = mediaFiles[0].fileName;
            formPost["thumbnailUrl"] = mediaFiles[0].thumbnailUrl;
            formPost["isVideo"] = mediaFiles[0].isVideo;

            if (!formPost["origUrl"]) {
              formPost["origUrl"] = mediaFiles[0].originalUrl;
              formPost["originalUrl"] = mediaFiles[0].originalUrl;
            }

            if (!formPost["originalUrl"]) {
              formPost["origUrl"] = mediaFiles[0].originalUrl;
              formPost["originalUrl"] = mediaFiles[0].originalUrl;
            }

            // Single asset posts don't have a media files unfortunately.
            if (mediaFiles.length === 1) {
              formPost["altText"] = mediaFiles[0].altText;
              formPost["origUrl"] = mediaFiles[0].originalUrl;
              formPost["originalUrl"] = mediaFiles[0].originalUrl;
            }
          }

          formPost["type"] = detectPostType(formPost);
          return;
        }
        // No default
      }

      // fields that should be applied to all platforms, not only selected one
      const allPlatformsFields = [
        FIELDS.WHEN,
        FIELDS.IS_NOW,
        FIELDS.POST_STATUS,
      ] as string[];

      const isUnlinkedClusterPost =
        form.view.selectedPlatform !== "LINKED" && form.platforms.length > 1;
      const isAllPlatformField = allPlatformsFields.includes(field);
      // Cluster post
      if (isUnlinkedClusterPost && !isAllPlatformField) {
        const selectedPlatform = form.view.selectedPlatform;
        if (!newPlatformFields[selectedPlatform]) {
          newPlatformFields[selectedPlatform] = {};
        }

        _.set(newPlatformFields[selectedPlatform], fieldString, value);
      } else {
        _.set(formPost, fieldString, value);
      }

      // Need to set fields that may not have been set prior
      if (field === FIELDS.IS_STORY && value === false) {
        formPost[FIELDS.CAPTION] = formPost[FIELDS.CAPTION] || "";
        formPost[FIELDS.FIRST_COMMENT] = formPost[FIELDS.FIRST_COMMENT] || "";
        formPost[FIELDS.USER_TAGS] = formPost[FIELDS.USER_TAGS] || [];
        formPost[FIELDS.PRODUCT_TAGS] = formPost[FIELDS.PRODUCT_TAGS] || [];
      }

      // May need to do a type update (i.e. story toggle)
      formPost["type"] = detectPostType(formPost);
    });

    form.platformFields = newPlatformFields;
    form.post = formPost;

    // Validate form after update
    const [errors, warnings] = validate(
      form.postState,
      form.post,
      form.platformFields,
      form.platforms,
      form.overlapSchedule
    );

    form.validation = {
      errors,
      warnings,
    };

    state.byId[postId] = form;
    return state;
  },
  [CHECK_INSTAGRAM_POST_OVERLAPPING.SUCCESS]: (
    state,
    { payload: { postId, data } }
  ) => {
    state.byId[postId].overlapSchedule = data;

    const form = state.byId[postId];

    const [errors, warnings] = validate(
      form.postState,
      form.post,
      form.platformFields,
      form.platforms,
      form.overlapSchedule
    );

    form.validation = {
      errors,
      warnings,
    };

    state.byId[postId] = form;

    return state;
  },
  [CHECK_INSTAGRAM_POST_OVERLAPPING.FAILURE]: (state, action) => {
    const { meta } = action;
    const postId = meta?.postId;
    if (postId && state.byId[postId]) {
      const form = state.byId[postId];
      form.validation.errors["OVERLAP_ERROR"] = {
        errors: [
          "Unable to verify Instagram posting time. Please try again or choose a different time.",
        ],
      };
      state.byId[postId] = form;
    }
    return state;
  },
  [setSaveData.type]: (
    state,
    action: {
      payload: { id: string; data: FormSaveData };
    }
  ) => {
    const { id, data } = action.payload;
    return updateStateById(state, id, { formSaveData: data, isDirty: false });
  },
  [setIsFormDirty.type]: (
    state,
    action: {
      payload: { id: string; isDirty: boolean };
    }
  ) => {
    const { id, isDirty } = action.payload;
    return updateStateById(state, id, { isDirty });
  },
  [setCoverPreviewUrl.type]: (
    state,
    action: {
      payload: { id: string; url: string };
    }
  ) => {
    const { id, url } = action.payload;
    return updateStateById(state, id, { coverPreviewUrl: url });
  },
});

function updateStateById(
  state: PostFormsState,
  id: string,
  newState: Partial<PostFormState>
): PostFormsState {
  if (id && state.byId[id]) {
    const form = state.byId[id];
    return {
      ...state,
      byId: {
        ...state.byId,
        [id]: { ...form, ...newState },
      },
    };
  }
  return state;
}

const buildInitialPost = (
  post: Post | LoadedDraft,
  accounts: AccountType[]
) => {
  const normalizedPost = extractCorePost(post);
  const accountIds = _.map(accounts, "_id");

  const postCopy = {
    ...normalizedPost,
    isStory: normalizedPost.type === POST_TYPES.STORY,
    accounts: accounts,
    accountIds: accountIds,
  };

  // because with drafts, post.schedule will not contain the ID
  if (!postCopy._id && post._id) {
    postCopy._id = post._id;
  }

  delete postCopy.postState;

  if (
    normalizedPost.when &&
    typeof normalizedPost.when === "string" &&
    normalizedPost.when.toLowerCase() === "invalid date"
  ) {
    postCopy.when = null;
  }

  // We use both of these but the previewer relies on originalUrl
  if (postCopy.origUrl && !postCopy.originalUrl) {
    postCopy["originalUrl"] = postCopy.origUrl;
  }

  // Some story posts don't set original url
  if (!postCopy.origUrl && !postCopy.originalUrl && postCopy.url) {
    postCopy["originalUrl"] = postCopy.url;
    postCopy["origUrl"] = postCopy.url;
  }

  if (
    _.isEmpty(postCopy.mediaFiles) && // There is a bug where a story that has a single video doesn't set isVideo
    postCopy.isStory &&
    postCopy.originalUrl?.includes(".mp4")
  ) {
    postCopy.isVideo = true;
  }

  return postCopy;
};

const getVisibleFields = (
  selectedPlatform: FormPlatformType,
  platforms: PlatformSpec[]
) => {
  if (selectedPlatform === null) {
    return [];
  }
  let fields: string[] = [];
  if (selectedPlatform === "LINKED") {
    platforms.map((platform) => {
      const platformFields = postPlatformsConfig[platform.TYPE];
      if (platformFields) {
        // eslint-disable-next-line unicorn/prefer-spread
        fields = fields.concat(platformFields.fields);
      }
    });
  } else {
    fields = [...postPlatformsConfig[selectedPlatform].fields];
  }
  fields = _.uniq(fields);
  return fields;
};

const getByIdOrFirst = (state: PostFormsState, formId: string | undefined) => {
  const id = formId || state.allIds[0];
  return state.byId[id];
};

export default postFormsReducer;
